import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, ArrowLeft, SearchX } from 'lucide-react';

import Link from '@/components/ui/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import IconArchireportAndText from '@/components/icons/iconArchireportAndText';

import { getPaymentIntent } from '@/utils/api';
import i18n from '@/utils/i18n';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/authContext';

type PaymentStatus = 'succeeded' | 'processing' | 'requires_payment_method' | 'canceled' | 'loading';

interface PaymentDetails {
  id: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  created: number;
  description?: string;
  receipt_email?: string;
}

type StatusConfig = {
  status: PaymentStatus;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  description2?: string;
  iconColor: string;
  button?: {
    text: string;
    action?: () => void;
  };
  buttonVariant: 'default' | 'outline';
};

const fetchPaymentIntent = async (
  setupIntentId: string
): Promise<{
  status: PaymentStatus;
  last_payment_error?: {
    message: string;
    code?: string;
  };
}> => {
  const result = (await getPaymentIntent(setupIntentId)) as PaymentDetails;

  if (!result || !result.id) {
    throw new Error('Invalid payment intent ID');
  }
  return result;
};

const getStatusConfig = (status: PaymentStatus): StatusConfig => {
  switch (status) {
    case 'succeeded':
      return {
        status,
        icon: CheckCircle,
        title: i18n.t('Payment_SuccessTitle'),
        description: i18n.t('Payment_SuccessContent1'),
        description2: i18n.t('Payment_SuccessContent2'),
        iconColor: 'text-primary', // theme primary color
        buttonVariant: 'default' as const,
      };
    case 'processing':
      return {
        status,
        icon: Clock,
        title: i18n.t('Payment_ProcessingTitle'),
        description: i18n.t('Payment_ProcessingContent1'),
        description2: i18n.t('Payment_ProcessingContent2'),
        iconColor: 'text-accent', // theme accent color
        button: {
          text: i18n.t('Global_ReloadPage'),
          action: () => {
            window.location.reload();
          },
        },
        buttonVariant: 'default' as const,
      };
    case 'requires_payment_method':
      return {
        status,
        icon: XCircle,
        title: i18n.t('Payment_FailedTitle'),
        description: i18n.t('Payment_FailedContent'),
        iconColor: 'text-red-500',
        button: {
          text: i18n.t('Global_Try_Again'),
          action: () => {
            window.history.back();
          },
        },
        buttonVariant: 'default' as const,
      };
    case 'canceled':
      return {
        status,
        icon: AlertCircle,
        title: i18n.t('payment_canceled'),
        description: i18n.t('payment_canceled_description'),
        iconColor: 'text-gray-500',
        button: {
          text: i18n.t('Global_Try_Again'),
          action: () => {
            window.history.back();
          },
        },
        buttonVariant: 'default' as const,
      };
    default:
      return {
        status,
        icon: Clock,
        title: i18n.t('Payment_Processing_Label'),
        description: i18n.t('Payment_Processing'),
        description2: i18n.t('Payment_ProcessingContent1'),
        iconColor: 'text-gray-400',
        button: {
          text: 'Refresh Status',
          action: () => {
            window.location.reload();
          },
        },
        buttonVariant: 'outline' as const,
      };
  }
};

export default function StatusPage() {
  const { t } = useTranslation();
  const { region } = useAuthContext();

  const paymentIntentId = new URLSearchParams(window.location.search).get('payment_intent');
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [errorDetails, setErrorDetails] = useState<{ message: string; code?: string } | undefined>(undefined);

  useEffect(() => {
    if (paymentIntentId) {
      fetchPaymentIntent(paymentIntentId)
        .then(paymentIntent => {
          setStatus(paymentIntent.status as PaymentStatus);
          if (paymentIntent.last_payment_error) {
            setErrorDetails({
              message: paymentIntent.last_payment_error.message,
              code: paymentIntent.last_payment_error.code,
            });
          }
        })
        .catch(error => {
          console.error('Failed to fetch payment intent:', error);
          setStatus('canceled');
        });
    } else {
      setStatus('canceled');
    }
  }, [paymentIntentId]);

  function backToArchireport() {
    if (navigator.userAgent.toLowerCase().indexOf(' electron/') > -1) {
      window.opener.postMessage('start-sync', '*');
      window.close();
    } else {
      window.location.href = `${region?.web}/web/subscription`;
    }
  }

  if (!paymentIntentId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg bg-card border-border dark:bg-card dark:border-border">
          <CardContent className="pt-12 pb-8 px-8 text-center space-y-6">
            <SearchX className="w-14 h-14 text-muted-foreground mx-auto" />

            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-foreground">Invalid Payment Link</h1>
              <p className="text-muted-foreground">This payment link is invalid or has expired.</p>
            </div>

            <Button asChild className="w-full">
              <Link href="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('Global_Back')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const config = getStatusConfig(status);
  const StatusIcon = config.icon;
  const isLoading = status === 'loading';

  return (
    <div className="min-h-screen bg-gradient-to-br from-card to-muted flex items-center justify-center p-4 flex-col">
      {/* Logo */}
      <div className="flex justify-center pb-5 h-20">
        <div className="flex items-center gap-2">
          <IconArchireportAndText width={212} height={48} className="h-8" />
        </div>
      </div>

      <Card className="w-full max-w-md min-h-96 bg-card border-border dark:bg-background rounded-lg border">
        <CardContent className="pt-12 pb-8 px-8 text-center space-y-8">
          {/* Status Content */}
          <div className="space-y-6">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="w-14 h-14 rounded-full mx-auto bg-muted" />
                <div className="space-y-2">
                  <Skeleton className="h-8 w-48 mx-auto bg-muted" />
                  <Skeleton className="h-4 w-64 mx-auto bg-muted" />
                </div>
              </div>
            ) : (
              <>
                <StatusIcon className={`w-14 h-14 ${config.iconColor} mx-auto`} />

                <div className="space-y-3">
                  <h1 className="text-2xl font-bold text-foreground">{config.title}</h1>

                  <p className="text-muted-foreground leading-relaxed">{config.description}</p>
                  {config.description2 && (
                    <p className="text-muted-foreground leading-relaxed">{config.description2}</p>
                  )}

                  {errorDetails && (
                    <div className="bg-muted border border-destructive rounded-md p-4 mt-2 text-left mx-auto">
                      <pre className="text-xs text-destructive font-mono whitespace-pre-wrap break-words">
                        {errorDetails.message}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Action Button */}
          <div className="space-y-4">
            {!isLoading && config.button && (
              <Button
                variant={config.buttonVariant}
                className="w-full h-12 text-sm font-medium"
                {...(config.button.action ? { onClick: config.button.action } : {})}
              >
                {config.button.text}
              </Button>
            )}

            {!isLoading && (
              <Button variant="ghost" size="sm" onClick={() => backToArchireport()}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {i18n.t('Payment_BackToArchireport')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
